<template>
  <div>
    <section class="hero is-fullheight">
      <div class="hero-body">
        <div class="container has-text-centered">
          <h3 class="title has-text-white">{{ titulo }}</h3>
          <div class="box app-justification-box">
            <form @submit.prevent="confirmar">
              <div class="columns">
                <div class="column">
                  <b-field label="Data Limite Razões">
                    <b-datepicker
                      v-model="datas.razoes"
                      editable
                      placeholder="Data Limite Razões"
                      icon="calendar-today"
                      v-mascara:data
                      :disabled="campoDesabilitado('razoes')"
                      :min-date="minRazoes"
                      :unselectable-days-of-week="diasNaoSelecionaveis"
                      :unselectable-dates="feriadosSelecao"
                      @input="atualizarEncadeamento('razoes')"
                      onkeypress="return event.charCode >= 48 && event.charCode <= 57"
                    />
                  </b-field>
                </div>
                <div class="column">
                  <b-field label="Data Limite Contrarrazões">
                    <b-datepicker
                      v-model="datas.contrarrazoes"
                      editable
                      placeholder="Data Limite Contrarrazões"
                      icon="calendar-today"
                      v-mascara:data
                      :disabled="campoDesabilitado('contrarrazoes')"
                      :min-date="minContrarrazoes"
                      :unselectable-days-of-week="diasNaoSelecionaveis"
                      :unselectable-dates="feriadosSelecao"
                      @input="atualizarEncadeamento('contrarrazoes')"
                      onkeypress="return event.charCode >= 48 && event.charCode <= 57"
                    />
                  </b-field>
                </div>
                <div class="column">
                  <b-field label="Data Limite Julgamento">
                    <b-datepicker
                      v-model="datas.julgamento"
                      editable
                      placeholder="Data Limite Julgamento"
                      icon="calendar-today"
                      v-mascara:data
                      :disabled="campoDesabilitado('julgamento')"
                      :min-date="minJulgamento"
                      :unselectable-days-of-week="diasNaoSelecionaveis"
                      :unselectable-dates="feriadosSelecao"
                      onkeypress="return event.charCode >= 48 && event.charCode <= 57"
                    />
                  </b-field>
                </div>
              </div>

              <div class="resumo-etapas">
                <table class="table is-narrow is-fullwidth is-striped resumo-etapas__tabela">
                  <tbody>
                    <tr v-for="linha in resumoEtapas" :key="linha.etapa">
                      <td class="resumo-etapas__etapa">{{ linha.etapa }}</td>
                      <td class="resumo-etapas__periodo">{{ formatarPeriodoEtapa(linha) }}</td>
                    </tr>
                  </tbody>
                </table>
                <p class="resumo-etapas__legenda">(Horário local)</p>
              </div>

              <div class="buttons-container">
                <button
                  class="button is-primary rm-1"
                  type="button"
                  :class="{ 'is-loading': loading }"
                  :disabled="loading || !podeConfirmar"
                  @click.prevent="confirmar"
                >
                  Confirmar
                </button>
                <button class="button" type="button" :disabled="loading" @click.prevent="fechar">
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import {
  addBusinessDays,
  dateAtNoon,
  feriadosNacionais,
  fimDoDiaPrazo,
  maxDate,
  montarResumoEtapasRecursais,
  PRAZO_CONSIDERACAO_JULGAMENTO_DIAS_UTEIS,
  PRAZO_CONTRARRAZOES_DIAS_UTEIS,
  PRAZO_RAZOES_DIAS_UTEIS,
  startOfDay,
} from '@/utils/businessDays';
import { formatarDataSegundo } from '@/utils/format';

const toIsoDate = date => {
  if (!date) return null;
  const d = new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export default {
  name: 'PrazosRecursaisDialog',
  props: {
    titulo: {
      type: String,
      default: 'Definir Prazos',
    },
    ajuste: {
      type: Boolean,
      default: false,
    },
    regrasAjuste: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    sugestoes: {
      type: Object,
      default: () => ({}),
    },
    linhaDoTempo: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      feriadosNacionais,
      instanteAbertura: new Date(),
      datas: {
        razoes: null,
        contrarrazoes: null,
        julgamento: null,
      },
    };
  },
  computed: {
    prazosLivres() {
      return process.env.VUE_APP_ENABLE_RETROACTIVE_DATE === 'true';
    },
    diasNaoSelecionaveis() {
      return this.prazosLivres ? [] : [0, 6];
    },
    feriadosSelecao() {
      return this.prazosLivres ? [] : this.feriadosNacionais;
    },
    podeConfirmar() {
      if (!this.ajuste) return true;

      return ['razoes', 'contrarrazoes', 'julgamento'].some(
        campo => !this.campoDesabilitado(campo),
      );
    },
    minRazoes() {
      if (this.campoDesabilitado('razoes')) return null;

      const persistidoAjuste = this.dataPersistidaMinima('razoes');

      if (this.prazosLivres) {
        const minimoLivre = startOfDay(new Date());
        return dateAtNoon(persistidoAjuste ? maxDate(minimoLivre, persistidoAjuste) : minimoLivre);
      }

      const minimoLegal = addBusinessDays(startOfDay(new Date()), PRAZO_RAZOES_DIAS_UTEIS);

      if (!this.ajuste) {
        return dateAtNoon(minimoLegal);
      }

      return dateAtNoon(persistidoAjuste ? maxDate(minimoLegal, persistidoAjuste) : minimoLegal);
    },
    minContrarrazoes() {
      if (this.campoDesabilitado('contrarrazoes')) return null;

      const referenciaRazoes = this.datas.razoes || this.minRazoes;
      if (!referenciaRazoes) return null;

      const persistidoAjuste = this.dataPersistidaMinima('contrarrazoes');

      if (this.prazosLivres) {
        const minimoLivre = startOfDay(referenciaRazoes);
        return dateAtNoon(persistidoAjuste ? maxDate(minimoLivre, persistidoAjuste) : minimoLivre);
      }

      const minimoLegal = addBusinessDays(referenciaRazoes, PRAZO_CONTRARRAZOES_DIAS_UTEIS);

      if (!this.ajuste) {
        return dateAtNoon(minimoLegal);
      }

      return dateAtNoon(persistidoAjuste ? maxDate(minimoLegal, persistidoAjuste) : minimoLegal);
    },
    minJulgamento() {
      if (this.campoDesabilitado('julgamento')) return null;

      const referenciaContrarrazoes = this.datas.contrarrazoes || this.minContrarrazoes;
      if (!referenciaContrarrazoes) return null;

      const persistidoAjuste = this.dataPersistidaMinima('julgamento');

      if (this.prazosLivres) {
        const minimoLivre = startOfDay(referenciaContrarrazoes);
        return dateAtNoon(persistidoAjuste ? maxDate(minimoLivre, persistidoAjuste) : minimoLivre);
      }

      const minimoLegal = addBusinessDays(
        referenciaContrarrazoes,
        PRAZO_CONSIDERACAO_JULGAMENTO_DIAS_UTEIS,
      );

      if (!this.ajuste) {
        return dateAtNoon(minimoLegal);
      }

      return dateAtNoon(persistidoAjuste ? maxDate(minimoLegal, persistidoAjuste) : minimoLegal);
    },
    d0Referencia() {
      if (this.ajuste && this.linhaDoTempo?.d0) {
        return new Date(this.linhaDoTempo.d0);
      }

      return this.instanteAbertura;
    },
    resumoEtapas() {
      return montarResumoEtapasRecursais({
        d0: this.d0Referencia,
        fimRazoes: this.valorEfetivoData('razoes'),
        fimContrarrazoes: this.valorEfetivoData('contrarrazoes'),
        fimJulgamento: this.valorEfetivoData('julgamento'),
      });
    },
  },
  watch: {
    sugestoes: {
      immediate: true,
      handler(sugestoes) {
        if (!this.ajuste) {
          this.instanteAbertura = new Date();
        }
        this.preencherSugestoes(sugestoes);
      },
    },
    linhaDoTempo: {
      immediate: true,
      handler() {
        if (this.ajuste) {
          this.preencherSugestoes(this.sugestoes);
        }
      },
    },
  },
  methods: {
    /** Em ajuste, a data mínima nunca pode ser anterior à persistida (não antecipa no diálogo). */
    dataPersistidaMinima(campo) {
      if (!this.ajuste) return null;
      const valor = this.valorPersistidoCampo(campo);
      return valor ? startOfDay(valor) : null;
    },
    /** Em ajuste, prioriza timestamps da linha do tempo (banco); senão, dataLimite*. */
    valorPersistidoCampo(campo) {
      const mapaLinha = {
        razoes: this.linhaDoTempo?.razoesAte,
        contrarrazoes: this.linhaDoTempo?.contrarrazoesAte,
        julgamento: this.linhaDoTempo?.consideracaoAte,
      };
      const daLinha = mapaLinha[campo];
      if (daLinha) return new Date(daLinha);
      if (this.sugestoes[campo]) return new Date(this.sugestoes[campo]);
      return null;
    },
    valorEfetivoData(campo) {
      if (this.campoDesabilitado(campo)) {
        return this.valorPersistidoCampo(campo);
      }

      // Datas escolhidas no datepicker são dia civil → fim do dia no resumo
      return this.datas[campo] ? fimDoDiaPrazo(this.datas[campo]) : null;
    },
    formatarPeriodoEtapa({ inicio, fim }) {
      const inicioFormatado = inicio ? formatarDataSegundo(inicio) : '—';
      const fimFormatado = fim ? formatarDataSegundo(fim) : '—';

      return `${inicioFormatado} a ${fimFormatado}`;
    },
    campoDesabilitado(campo) {
      if (!this.ajuste || !this.regrasAjuste?.[campo]) return false;
      return this.regrasAjuste[campo].vencido;
    },
    dataAnteriorAoMinimo(data, minimo) {
      if (!data || !minimo) return false;
      return startOfDay(data).getTime() < startOfDay(minimo).getTime();
    },
    atualizarEncadeamento(campo) {
      if (
        campo === 'razoes' &&
        !this.campoDesabilitado('contrarrazoes') &&
        this.datas.contrarrazoes &&
        this.minContrarrazoes &&
        this.dataAnteriorAoMinimo(this.datas.contrarrazoes, this.minContrarrazoes)
      ) {
        this.datas.contrarrazoes = new Date(this.minContrarrazoes);
      }

      if (
        (campo === 'razoes' || campo === 'contrarrazoes') &&
        !this.campoDesabilitado('julgamento') &&
        this.datas.julgamento &&
        this.minJulgamento &&
        this.dataAnteriorAoMinimo(this.datas.julgamento, this.minJulgamento)
      ) {
        this.datas.julgamento = new Date(this.minJulgamento);
      }
    },
    preencherSugestoes(sugestoes = {}) {
      this.datas = {
        razoes: sugestoes.razoes ? dateAtNoon(new Date(sugestoes.razoes)) : null,
        contrarrazoes: sugestoes.contrarrazoes
          ? dateAtNoon(new Date(sugestoes.contrarrazoes))
          : null,
        julgamento: sugestoes.julgamento ? dateAtNoon(new Date(sugestoes.julgamento)) : null,
      };

      // Garante que ajuste nunca fique abaixo do mínimo (não antecipa no diálogo).
      if (this.ajuste) {
        this.$nextTick(() => {
          ['razoes', 'contrarrazoes', 'julgamento'].forEach(campo => {
            if (this.campoDesabilitado(campo) || !this.datas[campo]) return;
            const minimo = this.minimoCampo(campo);
            if (minimo && this.dataAnteriorAoMinimo(this.datas[campo], minimo)) {
              this.datas[campo] = new Date(minimo);
            }
          });
        });
      }
    },
    minimoCampo(campo) {
      if (campo === 'razoes') return this.minRazoes;
      if (campo === 'contrarrazoes') return this.minContrarrazoes;
      return this.minJulgamento;
    },
    fechar() {
      this.$parent.onCancel();
      this.$parent.close();
    },
    confirmar() {
      if (!this.podeConfirmar) return;

      if (this.ajuste && this.temAntecipacaoNoAjuste()) {
        return;
      }

      const payload = {};

      // No ajuste, só envia campos editáveis — vencidos/antecipados permanecem no banco.
      ['razoes', 'contrarrazoes', 'julgamento'].forEach(campo => {
        if (this.ajuste && this.campoDesabilitado(campo)) return;

        const chave = {
          razoes: 'dataLimiteRazoes',
          contrarrazoes: 'dataLimiteContrarrazoes',
          julgamento: 'dataLimiteJulgamento',
        }[campo];

        const valor = this.datas[campo];
        if (valor) {
          payload[chave] = toIsoDate(valor);
        }
      });

      this.$parent.$emit('confirmar-prazos-recursais', payload);
    },
    temAntecipacaoNoAjuste() {
      return ['razoes', 'contrarrazoes', 'julgamento'].some(campo => {
        if (this.campoDesabilitado(campo)) return false;
        const escolhida = this.datas[campo];
        const persistida = this.valorPersistidoCampo(campo);
        if (!escolhida || !persistida) return false;
        return startOfDay(escolhida).getTime() < startOfDay(persistida).getTime();
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.buttons-container {
  margin-top: 1rem;
  text-align: center;
}

.app-justification-box {
  width: max-content;
  max-width: min(960px, 95vw);
  min-width: min(720px, 95vw);
  text-align: left;
}

.rm-1 {
  margin-right: 3px;
}

.resumo-etapas {
  margin-top: 1.25rem;
  margin-bottom: 0.25rem;
}

.resumo-etapas__tabela {
  margin-bottom: 0;
}

.resumo-etapas__etapa {
  width: 38%;
  font-weight: normal;
  white-space: nowrap;
}

.resumo-etapas__periodo {
  text-align: right;
}

.resumo-etapas__legenda {
  margin-top: 0.35rem;
  font-size: 0.85rem;
  color: #8b8b8b;
  text-align: right;
}

@media (max-width: 768px) {
  .app-justification-box {
    min-width: unset;
    width: 100%;
    max-width: 90vw;
  }
}
</style>
